import { ACDLError } from '__legacy__/models/error.model';
import { useTranslator } from '@Localization';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ConfirmationModalWithTextBox } from '../../../../__legacy__/components/ConfirmationModalWithTextBox';
import { withPolling } from '../../../../__legacy__/helpers/polling.helpers';
import { useLoader } from '../../../../__legacy__/helpers/useLoader';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { S_ConfirmationModal } from '../../../../design/modals/confirmationModal/S_ConfirmationModal';
import { useCloudCostsService } from '../../../cloudCosts/cloudCosts.service';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { IOverlayLoaderStore } from '../../../common/OverlayLoader.store';
import { Settings } from '../../../settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { JOB_START_MODAL_TYPE, JobStartModal, START_MODAL_LOADER_KEY } from '../../components/JobStartModal';
import { JobsList } from '../../components/list/JobsList';
import { IJobDetailsService } from '../../jobDetails.service';
import { useJobPermissions } from '../../jobPermissions';
import { IJobListItem, JOB_OWNERSHIP, JOB_TYPE } from '../../jobs.model';
import { IJobsService } from '../../jobs.service';
import { getJobRoutePrefix, getJobTranslationPrefix } from '../../utils';
import { JobsTabsContainer } from './JobsTabs.container';

interface IJobsListContainerProps {
  jobType: JOB_TYPE;
}

enum MODAL_TYPE {
  STOP = 'STOP',
  RESTART = 'RESTART',
  DELETE = 'DELETE',
}

export const JobsListContainer = observer(({ jobType }: IJobsListContainerProps): ReactElement => {
  const jobsService = useInjection(IJobsService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const jobDetailsService = useInjection(IJobDetailsService.$);
  const cloudCostsService = useCloudCostsService();
  const { t } = useTranslator();
  const translationPrefix = getJobTranslationPrefix(jobType);
  const context = useContext(Settings);
  const location = useLocation();
  const history = useHistory();
  const jobPermissions = useJobPermissions();
  const config = useConfiguration();

  const { isLoading: isEstimating, withLoaderAsync } = useLoader();
  const [openedModal, setOpenedModal] = useState<MODAL_TYPE | null>(null);
  const [actionTarget, setActionTarget] = useState<IJobListItem | null>(null);
  const pollingRef = useRef<() => void>(null);
  const actionModalLoader = 'job-action-modal-loader';

  const { store, downloadJobAsync } = jobsService;
  const { jobs, paging } = store;
  const { pageNumber, totalCount, orderType, orderBy } = paging;
  const { costsUsage } = cloudCostsService.store;
  const { estimatedCost } = jobDetailsService.store;

  const isLoading = overlayLoader.isSpinnerVisible('jobs-list');

  const clearPolling = (): void => pollingRef.current?.();
  const initPolling = (): (() => void) => {
    overlayLoader.enableLoader('jobs-list');
    refresh();
    return clearPolling;
  };

  useEffect(() => {
    store.resetStore(jobType);

    return clearPolling;
  }, [currentWorkspaceStore.currentWorkspace.id]);

  useEffect(() => {
    if (!checkAccess()) {
      return clearPolling;
    }

    return initPolling();
  }, [currentWorkspaceStore.currentWorkspace.id, context.store.jobsPageSize, pageNumber, orderType, orderBy, store.ownership]);

  useEffect(() => {
    let ownership: JOB_OWNERSHIP = JOB_OWNERSHIP.All;
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;

    switch (location.pathname) {
      case getJobRoutePrefix(jobType).List.All.withParams({ workspaceId }):
        ownership = JOB_OWNERSHIP.All;
        break;
      case getJobRoutePrefix(jobType).List.Owned.withParams({ workspaceId }):
        ownership = JOB_OWNERSHIP.My;
        break;
      case getJobRoutePrefix(jobType).List.Others.withParams({ workspaceId }):
        ownership = JOB_OWNERSHIP.Others;
        break;
    }

    jobsService.changeOwnershipFilter(ownership);
  }, [location.pathname, currentWorkspaceStore.currentWorkspace.id]);

  const handleOrderChange = (orderBy: string, orderType: string): void => {
    jobsService.changeOrder(orderBy, orderType || 'asc');
  };

  const handlePaginationChange = (pageNumber: number, pageSize: number): void => {
    jobsService.changePagination(pageNumber, pageSize);
  };

  const handleChange = (pageNumber: number, pageSize: number): void => {
    context.setJobsPageSize(pageSize);
    handlePaginationChange(pageNumber, pageSize);
  };

  const handleStartJobAction = async (modalType: MODAL_TYPE, jobItem: IJobListItem): Promise<void> => {
    if (config.appConfig.runLocally) {
      await handleConfirmJobAction(jobItem, modalType);
    } else {
      if (modalType === MODAL_TYPE.RESTART) {
        await withLoaderAsync(async () => {
          jobDetailsService.store.resetStore(false, jobType);
          // We don't need to check permissions as the restart button is hidden for roles
          // without access to costs
          await Promise.allSettled([
            cloudCostsService.getCloudCostsUsageAsync(currentWorkspaceStore.currentWorkspace.id),
            jobDetailsService.populateJobDetailsAsync(jobItem.id),
          ]);
          const showDialog = await jobDetailsService.estimateCostAsync();

          if (!showDialog) {
            handleCancelJobAction();
          }
        });
      }
      setOpenedModal(modalType);
      setActionTarget(jobItem);
    }
  };

  const handleConfirmJobAction = async (target: IJobListItem, openedModal: MODAL_TYPE | null): Promise<void> => {
    if (!target) {
      throw new ACDLError("Confirm shouldn't be called without target");
    }

    switch (openedModal) {
      case MODAL_TYPE.DELETE:
        overlayLoader.enableLoader(actionModalLoader);
        await jobsService.deleteJobAsync(target.id);
        overlayLoader.disableLoader(actionModalLoader);
        break;
      case MODAL_TYPE.RESTART:
        overlayLoader.enableLoader(START_MODAL_LOADER_KEY);
        await jobsService.restartJobAsync(target.id);
        // Clean up store as it contains data loaded for restart purposes
        jobDetailsService.store.resetStore(false, jobType);
        overlayLoader.disableLoader(START_MODAL_LOADER_KEY);
        break;
      case MODAL_TYPE.STOP:
        overlayLoader.enableLoader(actionModalLoader);
        await jobsService.stopJobAsync(target.id);
        overlayLoader.disableLoader(actionModalLoader);
        break;
      case null: {
        break;
      }
    }
    setOpenedModal(null);
    setActionTarget(null);
  };

  const handleCancelJobAction = (): void => {
    setOpenedModal((modal) => {
      // Clean up store as it might contain data loaded for restart purposes
      if (modal === MODAL_TYPE.RESTART) {
        jobDetailsService.store.resetStore(false, jobType);
      }

      return null;
    });
    setActionTarget(null);
  };

  const handleJobEdit = (jobItem: IJobListItem): void => {
    const route =
      jobItem.jobType === JOB_TYPE.TRAINING ? getJobRoutePrefix(jobItem.jobType).Details.Settings : getJobRoutePrefix(jobItem.jobType).Details.Datasets;
    history.push(
      route.withParams({
        workspaceId: currentWorkspaceStore.currentWorkspace.id,
        jobId: jobItem.id,
      })
    );
  };

  const checkAccess = (): boolean => {
    const canView = jobPermissions.canList().allowed && !currentWorkspaceStore.currentWorkspace.encryption.encrypted;

    if (!canView) {
      history.push(Home.Projects.List.All.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
    }

    return canView;
  };

  const refresh = (): void => {
    clearPolling();

    pollingRef.current = withPolling(async (): Promise<void> => {
      await jobsService.refreshAsync(false);
    }, 15000);
  };

  let actionTargetName = '';
  if (actionTarget) {
    actionTargetName = actionTarget.jobType === JOB_TYPE.TRAINING ? actionTarget.trainingName : actionTarget.inferenceName;
  }

  const confirmationDeleteQuestion = (
    <Trans
      i18nKey={`${translationPrefix}.delete.confirmation`}
      values={{ name: actionTargetName }}
      components={{ b: <b /> }}
    />
  );

  return (
    <ColumnLayout size='none'>
      <JobsTabsContainer jobType={jobType} />
      <JobsList
        workspaceId={currentWorkspaceStore.currentWorkspace.id}
        jobType={jobType}
        jobs={jobs}
        isLoading={isLoading}
        pagination={{
          pageNumber,
          totalCount,
          pageSize: context.store.jobsPageSize,
          onChange: handleChange,
        }}
        sorting={{
          orderBy: orderBy || '',
          orderType: orderType || '',
        }}
        downloadJobAsync={downloadJobAsync}
        onRestartJob={async (jobItem: IJobListItem) => {
          await handleStartJobAction(MODAL_TYPE.RESTART, jobItem);
        }}
        onStopJob={async (jobItem: IJobListItem) => {
          await handleStartJobAction(MODAL_TYPE.STOP, jobItem);
        }}
        onDeleteJob={async (jobItem: IJobListItem) => {
          await handleStartJobAction(MODAL_TYPE.DELETE, jobItem);
        }}
        onEditJob={handleJobEdit}
        onOrderingChange={handleOrderChange}
      />
      {actionTarget && (
        <>
          <JobStartModal
            isOpen={openedModal === MODAL_TYPE.RESTART}
            availableFunds={costsUsage ? costsUsage.limit - costsUsage.usage : 0}
            estimatedCost={estimatedCost}
            isLoading={overlayLoader.isSpinnerVisible(START_MODAL_LOADER_KEY)}
            isOverlayVisible={isEstimating}
            jobType={jobType}
            onSubmit={async () => {
              await handleConfirmJobAction(actionTarget, openedModal);
            }}
            status={actionTarget.status}
            toggle={handleCancelJobAction}
            type={JOB_START_MODAL_TYPE.RESTART}
          />

          <S_ConfirmationModal
            isOpen={openedModal === MODAL_TYPE.STOP}
            headerText={t(`${translationPrefix}.stop.text`)}
            submitText={t('confirm')}
            cancelText={t('cancel')}
            onCancel={handleCancelJobAction}
            toggle={handleCancelJobAction}
            onSubmit={async () => {
              await handleConfirmJobAction(actionTarget, openedModal);
            }}
            loaderKey={actionModalLoader}
            isLoading={overlayLoader.isSpinnerVisible(actionModalLoader)}
          >
            <p>{t(`${translationPrefix}.stop.info`)}</p>
          </S_ConfirmationModal>
          <ConfirmationModalWithTextBox
            showModal={openedModal === MODAL_TYPE.DELETE}
            confirmationHeader={t(`${translationPrefix}.delete.text`)}
            confirmationQuestion={t(`${translationPrefix}.delete.info`)}
            confirmationTextHeader={confirmationDeleteQuestion}
            confirmationText={actionTargetName}
            onCancel={handleCancelJobAction}
            onConfirm={async () => {
              await handleConfirmJobAction(actionTarget, openedModal);
            }}
            loaderKey={actionModalLoader}
            confirmationYes={t('delete')}
          />
        </>
      )}
    </ColumnLayout>
  );
});
